import { Box, styled, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Stack } from '@mui/system';
import { ChevronLeftIcon, ChevronRightIcon } from '@tix-ticketing/ui-icons/outline';
import { addMonths, isAfter, isBefore, isSameDay, isSameMonth, startOfMonth, startOfToday } from 'date-fns';
import { Fragment } from 'react';
import { FormattedDate } from 'react-intl';
import { SimpleIconButton } from 'components/common/buttons/simple-icon-button';
import { useAppState } from 'lib/app-state';
import { getMonthName } from 'lib/helpers/calendar-helpers';
import { clientTimezone } from 'lib/helpers/date-helpers';
import { Day, useCalendar } from 'lib/hooks/use-calendar';
export type CalendarRange = [Date | null, Date | null];
const Weekdays = styled(Box)`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
`;
const Cells = styled(Box)`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  row-gap: 8px;
`;

/**
 * Normalises a range so the earliest date is first. While the customer is
 * mid-selection (only a start picked) the second slot stays null.
 */
function orderRange(a: Date | null, b: Date | null): CalendarRange {
  if (a && b) return isAfter(a, b) ? [b, a] : [a, b];
  return [a, b];
}

/**
 * A two-month (single month on mobile) date-range picker. Mirrors the look of
 * the single Calendar but is larger and lets the customer pick a start and end
 * date, highlighting the days in between.
 *
 * Day cells are browser-local dates: the selected range is later turned into
 * DateFrom/DateTo query params, so no venue-timezone conversion happens here.
 */
export function CalendarMulti({
  startDate,
  endDate,
  onChange,
  minDate = startOfToday(),
  initialViewMonth
}: {
  startDate: Date | null;
  endDate: Date | null;
  onChange: (range: CalendarRange) => void;
  minDate?: Date;
  /**
   * Month to open on when no range is selected yet, e.g. the month of the
   * earliest event in the list. Falls back to the current month.
   */
  initialViewMonth?: Date | null;
}) {
  const theme = useTheme();
  const appState = useAppState();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const numberOfMonths = isMobile ? 1 : 2;
  const {
    calendar,
    viewing,
    viewPreviousMonth,
    viewNextMonth
  } = useCalendar({
    weekStartsOn: Day.MONDAY,
    numberOfMonths,
    viewing: startDate ?? initialViewMonth ?? minDate
  });
  const [rangeStart, rangeEnd] = orderRange(startDate, endDate);

  // Can't page earlier than the month that contains the earliest selectable day.
  const prevDisabled = !isAfter(startOfMonth(viewing), startOfMonth(minDate));
  const handleDayClick = (day: Date) => {
    // Starting fresh, or replacing a completed range, begins a new range.
    if (!startDate || startDate && endDate) {
      onChange([day, null]);
      return;
    }
    // A start exists and the end is open: the click completes the range,
    // reordering if the customer picked an earlier day second.
    onChange(orderRange(startDate, day));
  };
  return <Box sx={{
    width: '100%'
  }}>
      <Stack direction="row" sx={{
      gap: {
        xs: 2,
        sm: 4
      },
      alignItems: 'flex-start'
    }}>
        {calendar.map((weeks, monthIndex) => {
        const monthDate = startOfMonth(addMonths(viewing, monthIndex));
        const isFirst = monthIndex === 0;
        const isLast = monthIndex === calendar.length - 1;
        return <Box key={monthIndex} sx={{
          flex: 1,
          minWidth: 0
        }}>
              <Stack direction="row" sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
            minHeight: 32
          }}>
                <Box sx={{
              width: 32
            }}>
                  {isFirst && <SimpleIconButton ariaLabel="Previous month" icon={<ChevronLeftIcon />} onClick={viewPreviousMonth} disabled={prevDisabled} sx={{
                color: 'text.secondary'
              }} />}
                </Box>
                <Typography variant="h6">
                  {`${getMonthName(appState.locale, monthDate.getMonth(), 'long')} ${monthDate.getFullYear()}`}
                </Typography>
                <Box sx={{
              width: 32,
              textAlign: 'right'
            }}>
                  {isLast && <SimpleIconButton ariaLabel="Next month" icon={<ChevronRightIcon />} onClick={viewNextMonth} sx={{
                color: 'primary.main'
              }} />}
                </Box>
              </Stack>

              <Weekdays>
                {weeks[0]!.map(day => <Typography key={`${day}`} variant="body3" align="center" sx={{
              color: 'text.secondary',
              pb: 1.5
            }}>
                    <FormattedDate value={day} weekday="short" timeZone={clientTimezone} />
                  </Typography>)}
              </Weekdays>

              <Cells>
                {weeks.map((week, weekIndex) => <Fragment key={weekIndex}>
                    {week.map((day, dayIndex) => {
                const inMonth = isSameMonth(day, monthDate);
                const isPast = isBefore(day, minDate);
                const selectable = inMonth && !isPast;
                const isStart = !!rangeStart && isSameDay(day, rangeStart);
                const isEnd = !!rangeEnd && isSameDay(day, rangeEnd);
                const isBetween = !!rangeStart && !!rangeEnd && isAfter(day, rangeStart) && isBefore(day, rangeEnd);
                const isEndpoint = isStart || isEnd;
                // A committed range (two distinct days) draws a tinted
                // band with square edges. Between-cells are fully tinted;
                // each endpoint cell is tinted only on its inner half so
                // the band runs right up to the ring with no white gap,
                // while the ring's opaque fill keeps its inside clean.
                const hasRange = !!rangeStart && !!rangeEnd && !isSameDay(rangeStart, rangeEnd);
                const rangeTint = alpha(theme.palette.primary.main, 0.08);
                const cellBackground = !inMonth || !hasRange ? 'transparent' : isBetween ? rangeTint : isStart ? `linear-gradient(to right, transparent 50%, ${rangeTint} 50%)` : isEnd ? `linear-gradient(to left, transparent 50%, ${rangeTint} 50%)` : 'transparent';
                return <Box key={dayIndex} sx={{
                  height: 44,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: cellBackground
                }}>
                          {inMonth && <Box component={selectable ? 'button' : 'div'} onClick={selectable ? () => handleDayClick(day) : undefined} type={selectable ? 'button' : undefined} aria-pressed={isEndpoint || isBetween} sx={{
                    all: 'unset',
                    boxSizing: 'border-box',
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '999px',
                    cursor: selectable ? 'pointer' : 'default',
                    // Opaque fill on the endpoints masks the tint
                    // band inside the ring, so the highlight meets
                    // the circle's edge without colouring it in.
                    backgroundColor: isEndpoint ? 'background.paper' : 'transparent',
                    border: isEndpoint ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                    color: isPast ? 'text.disabled' : isBetween ? 'primary.main' : 'text.primary',
                    '&:hover': selectable ? {
                      borderColor: isEndpoint ? 'primary.main' : 'primary.light'
                    } : undefined,
                    '&:focus-visible': {
                      outline: `2px solid ${theme.palette.primary.main}`,
                      outlineOffset: 2
                    }
                  }}>
                              <Typography variant="body2Medium" component="span">
                                <FormattedDate value={day} day="numeric" timeZone={clientTimezone} />
                              </Typography>
                            </Box>}
                        </Box>;
              })}
                  </Fragment>)}
              </Cells>
            </Box>;
      })}
      </Stack>
    </Box>;
}